import Decimal from "decimal.js";
import {
  UpdatePositionValidation,
  DownloadRecentContractFilterValidation,
  DownloadFilterValidation,
  CreatePositionValidation,
  ContractListOptionParams,
  TradingAccountOpenPosition,
  TradingContract,
} from "../../lib/api/otc";
import { Customer } from "../../lib/api/reports";
import { safeParseDecimal } from "../../lib/api/common";

export interface NewContractInfo {
  contract_type: string;
  contract_no: string;

  date: Date;
  start_date?: Date;
  end_date?: Date;

  shipment_start_date?: Date;
  shipment_end_date?: Date;

  account_no_a: string;
  account_no_b?: string;

  weeks: string;
  period_qty: string;
  knockout: string;

  trade_reference: string;
  contract_reference?: string;
  customer_id: string;
  trader_id: string;

  quantity: number;
  commodity: string;

  option_month: string;
  step_one: string;
  step_two: string;
  step_three: string;
  double_up: string;

  option_premium: string;
  contract_fee: string;
  margin: string;
  margin_amount: string;

  crop_year: number;
}

export interface PositionListOptionParams {
  filters: {
    account_ids?: string[];
    strike_price?: string;
    commodity?: string;
    option_month?: string;
  };
}

export interface UpdateContractInfo extends Partial<NewContractInfo> {
  id: string;
}

export interface PairingData {
  position?: TradingAccountOpenPosition;
  contract?: TradingContract;
}

export interface MarginReportOptionParams {
  after_date?: Date;
  before_date?: Date;
}

export interface BearState {
  margin_report_filters?: MarginReportOptionParams;
  position_filters: PositionListOptionParams;
  editing_position_filters: PositionListOptionParams;
  contract_filters: ContractListOptionParams;
  editing_contract_filters: ContractListOptionParams;
  new_contract?: NewContractInfo;
  editing_contract?: UpdateContractInfo;
  hasAttemptedSave: boolean;
  contracts_filter_modal_status: boolean;
  positions_filter_modal_status: boolean;
  summaryFilters: undefined | {};
  open_contracts?: TradingContract[];
  open_positions?: TradingAccountOpenPosition[];
  downloadFilters?: typeof DownloadFilterValidation._type,
  downloadRecentContractFilters?: typeof DownloadRecentContractFilterValidation._type,
  edit_manual_position_modal_id?: string;

  setOpenContractModalDownloadStatus: (item: undefined | {}) => void;
  openContractModalDownloadStatus: undefined | {};

  hasDownloadsAttemptedSave: boolean,

  pairing_data: PairingData;
  contracts_position_modal_contract_id?: string;
  set_contracts_positions_modal_id: (id?: string | undefined) => void;

  new_position?: typeof CreatePositionValidation._type;
  edit_manual_position?: typeof UpdatePositionValidation._type
  hasPositionAttemptedSave: boolean;

  set_edit_manual_position_modal_id: (id?: string) => void;
  set_new_position: (new_position: {} | undefined) => void;
  set_edit_manual_position: (edit_position: {} | undefined) => void;
  set_margin_report_modal: (filters: {} | undefined) => void;

  update_margin_report_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;
  update_edit_position_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;
  updatePositionAttemptedSave: (status: boolean) => void;
  update_new_position_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;

  editDownloadRecentFilterKey: (
    key: string,
    value?: string | number | Date | undefined | null | any,
  ) => void;
  editDownloadFilterKey: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;

  setSummaryFilters: (new_filters: {} | undefined) => void;
  setDownloadFilters: (new_filters: {} | undefined) => void;
  setDownloadRecentContractsFilters: (new_filters: { created_date: Date } | undefined) => void;

  set_open_contracts: (data: TradingContract[]) => void;
  set_open_positions: (data: TradingAccountOpenPosition[]) => void;
  update_contract_filters_field: (
    key: string,
    value?: string | string[] | number | Date | undefined,
  ) => void;
  update_position_filters_field: (
    key: string,
    value?: string | string[] | number | Date | undefined,
  ) => void;
  set_position_filters: (key: string, value: string | undefined) => void;
  set_new_contract: (new_contract: {} | undefined) => void;
  set_edit_contract: (new_contract: {} | undefined) => void;
  set_pair_position: (pair_position: PairingData) => void;
  update_new_contract_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;
  update_edit_contract_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;
  updateAttemptedSave: (status: boolean) => void;
  set_contract_filter_modal_status: (status: boolean) => void;
  set_position_filter_modal_status: (status: boolean) => void;
  persistEditingContractFilters: () => void;
  persistPositionFilters: () => void;
  clear_filters: () => void;
}

export function ignoreSafeParseDecimal(item: string | number): Decimal {
  try {
    return new Decimal(item);
  } catch (error: any) {
    return new Decimal(0);
  }
}

type AdjSuffix = "" | "_two" | "_three";

function computeAdjMarginForSuffix(
  data: TradingContract,
  suffix: AdjSuffix
): Record<string, Decimal> {
  const contractFeeKey = `adj_contract_fee${suffix}` as keyof TradingContract;
  const optionPremiumKey = `adj_option_premium${suffix}` as keyof TradingContract;
  const marginKey = `adj_margin${suffix}`;
  const marginAmountKey = `adj_margin_amount${suffix}`;

  const adj_contract_fee = data[contractFeeKey] as Decimal | undefined;
  const adj_option_premium = data[optionPremiumKey] as Decimal | undefined;

  if (!adj_contract_fee || !adj_option_premium) {
    return {
      [marginKey]: new Decimal("0"),
      [marginAmountKey]: new Decimal("0"),
    };
  }

  const adj_margin = safeParseDecimal({})(adj_contract_fee || 0).sub(safeParseDecimal({})(adj_option_premium || 0));
  const adj_margin_amount = adj_margin.times(data.quantity || 0);

  return {
    [marginKey]: adj_margin,
    [marginAmountKey]: adj_margin_amount,
  };
}

export function computeAdjMargin(data: TradingContract) {
  return {
    ...computeAdjMarginOne(data),
    ...computeAdjMarginTwo(data),
    ...computeAdjMarginThree(data),
  };
}

/**
 * Computes adjusted margin and margin amount from adj_contract_fee and adj_option_premium.
 */
export function computeAdjMarginOne(data: TradingContract) {
  return computeAdjMarginForSuffix(data, "");
}

/**
 * Computes adjusted margin two and margin amount two from adj_contract_fee_two and adj_option_premium_two.
 */
export function computeAdjMarginTwo(data: TradingContract) {
  return computeAdjMarginForSuffix(data, "_two");
}

/**
 * Computes adjusted margin three and margin amount three from adj_contract_fee_three and adj_option_premium_three.
 */
export function computeAdjMarginThree(data: TradingContract) {
  return computeAdjMarginForSuffix(data, "_three");
}
